import { useQuery } from 'react-query';
import { useAxiosClient } from '../utils/useAxiosClient';

// Interface for the API response structure (can be shared or refined)
export interface BrandLoveData {
  blove_chart: {
    displayDates: string[];
    dates: string[];
    brands: string[]; // Corresponds to 'People' for personal_branding
    values: (number | null)[][];
  };
  post_count_chart: {
    dates: string[];
    brands: string[];
    values: (number | null)[][];
  };
  yearly_metrics: Record<string, { avg_blove: number; avg_engagement: number; post_volume: number }>;
  date_range_metrics: Record<string, { avg_blove: number; avg_engagement: number; post_volume: number }>;
  date_range: { start: string | null; end: string | null };
}

export const useInfluenceIndexData = (
  orgId: number,
  startDate?: string | null,
  endDate?: string | null,
  selectedPillars?: string[],
  isPersonalBranding: boolean = false
) => {
  const axiosClient = useAxiosClient();

  const queryParams = new URLSearchParams();
  // Use the formatted date strings directly if provided
  if (startDate) queryParams.set('date_min', startDate);
  if (endDate) queryParams.set('date_max', endDate);
  // Platforms filter is handled by backend based on dashboard type for this hook
  // Add pillars to query params
  if (selectedPillars && selectedPillars.length > 0) {
    selectedPillars.forEach(p => queryParams.append('pillars', p));
  }

  // Include pillars in the query key (add flag so react-query refetches when it changes)
  const queryKey = ['influenceIndexData', orgId, startDate, endDate, selectedPillars, isPersonalBranding];

  const fetchData = async (): Promise<BrandLoveData> => {
    // Pass personal_branding flag so backend can skip sqrt/scaling when requested
    if (isPersonalBranding) {
      queryParams.set('personal_branding', 'true');
    }

    const response = await axiosClient.get(`/api/brand-love?org=${orgId}&${queryParams.toString()}`);
    return response.data;
  };

  const { data, isLoading, error } = useQuery<BrandLoveData, Error>(
    queryKey,
    fetchData,
    {
      enabled: !!orgId, // Only run query when orgId is valid
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      // Keep previous data while fetching new data based on date changes
      keepPreviousData: true, 
    }
  );

  return {
    data,
    loading: isLoading,
    error: error ? error.message : null,
  };
}; 